import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import styled, { useTheme, css } from 'styled-components';
import { Clan, ClanMember, ClanRank, ClanAchievement, ACHIEVEMENT_RARITIES, CLAN_ROLES } from '../types/clan';
import { ClanNews } from '../types/clanNews';
import clanService from '../services/clanService';
import clanNewsService from '../services/clanNewsService';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import ClanNewsModal from '../components/clan/ClanNewsModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirmModal } from '../hooks/useConfirmModal';
import ClanNewsForm from '../components/clan/ClanNewsForm';
import ClanNewsTab from '../components/clan/ClanNewsTab';
import ClanAchievements from '../components/clan/ClanAchievements';
import ClanApplicationsTab from '../components/clan/ClanApplicationsTab';
import ClanInvitationsTab from '../components/clan/ClanInvitationsTab';
import StateSelector from '../components/common/StateSelector';
import GameFieldSelector from '../components/common/GameFieldSelector';
import MultiGameFieldSelector from '../components/common/MultiGameFieldSelector';
import ImageModal from '../components/common/ImageModal';
import InviteUserModal from '../components/common/InviteUserModal';
import { ALL_STATES_OPTION } from '../constants/states';
import ClanGamesSection from '../components/clan/ClanGamesSection';
import ModernBackground from '../components/common/ModernBackground';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};


const isShadowTheme = (theme: any) => theme.name === 'shadowGrid';
const isModernTheme = (theme: any) => theme.name === 'modernWarfare';
const isNightOps = (theme: any) => theme.name === 'nightOps';

const cardBorder = (theme: any) => theme.profile?.cards?.border || theme.colors.borderLight;

const ClanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifications, removeNotification, showSuccess, showError } = useNotification();
  const { confirm, modalProps } = useConfirmModal();
  const theme = useTheme();
  
  const isShadowGrid = theme.name === 'shadowGrid';
  const isModernWarfare = theme.name === 'modernWarfare';
  const isNightOpsMode = theme.name === 'nightOps';
  
  const [clan, setClan] = useState<Clan | null>(null);
  const [members, setMembers] = useState<ClanMember[]>([]);
  const [clanRanks, setClanRanks] = useState<ClanRank[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // URL-based tabs
  type TabType = 'overview' | 'members' | 'achievements' | 'news' | 'settings';
  type SubTabType = 'list' | 'applications' | 'invitations';
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabType) || 'overview';
  const memberSubTab = (searchParams.get('subtab') as SubTabType) || 'list';

  const setActiveTab = useCallback((tab: TabType) => {
    setSearchParams({ tab }, { replace: false });
  }, [setSearchParams]);

  const setMemberSubTab = useCallback((subtab: SubTabType) => {
    setSearchParams({ tab: activeTab, subtab }, { replace: false });
  }, [setSearchParams, activeTab]);
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [canManageMembers, setCanManageMembers] = useState(false);
  const [assigningOfficer, setAssigningOfficer] = useState<string | null>(null);
  const [removingOfficer, setRemovingOfficer] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [userApplications, setUserApplications] = useState<any[]>([]);
  const [additionalDataLoading, setAdditionalDataLoading] = useState(false);
  const [loadingInProgress, setLoadingInProgress] = useState(false);
  
  // Состояние для счетчиков в сайдбаре
  const [applicationsCount, setApplicationsCount] = useState(0);
  const [invitationsCount, setInvitationsCount] = useState(0);
  const [countersLoaded, setCountersLoaded] = useState(false);

  // Состояние для системы новостей
  const [overviewNews, setOverviewNews] = useState<{ publicNews: ClanNews | null; internalNews: ClanNews | null }>({ publicNews: null, internalNews: null });
  const [selectedNews, setSelectedNews] = useState<ClanNews | null>(null);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingNews, setEditingNews] = useState<ClanNews | undefined>(undefined);
  const [canManageNews, setCanManageNews] = useState(false);

  // Состояние для формы редактирования клана
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    tag: '',
    state: '',
    primaryBase: '',
    secondaryBases: [] as string[],
    logoFile: null as File | null
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Состояние для модального окна логотипа
  const [showLogoModal, setShowLogoModal] = useState(false);
  
  // Состояние для модального окна приглашения пользователей
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Состояния модальных окон
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  
  // Триггер для обновления достижений
  const [achievementsRefreshTrigger, setAchievementsRefreshTrigger] = useState(0);

  const loadClanData = useCallback(async () => {
    // Предотвращаем дублирующие вызовы
    if (loadingInProgress) {
      return;
    }

    try {
      setLoadingInProgress(true);
      setLoading(true);
      setError(null);
      
      // Загружаем только основную информацию клана для быстрой отрисовки
      const clanData = await clanService.getClanById(id!);
      
      
      setClan(clanData);
      
      // Сбрасываем состояние счетчиков при смене клана
      setCountersLoaded(false);
      setApplicationsCount(0);
      setInvitationsCount(0);
      
      // Инициализируем форму редактирования
      if (clanData) {
        setEditForm({
          name: clanData.name || '',
          description: clanData.description || '',
          tag: clanData.tag || '',
          state: clanData.state || '',
          primaryBase: clanData.primaryBase || '',
          secondaryBases: clanData.secondaryBases || [],
          logoFile: null
        });
      }
      
      setLoading(false);
      
      // Проверяем права управления сразу (синхронно)
      const isLeader = user && clanData.leaderId === user.id;
      const isSeniorOfficer = user && clanData.seniorOfficerId === user.id;
      const canManage = Boolean(isLeader || isSeniorOfficer);
      setCanManageMembers(canManage);
      setCanManageNews(canManage);
      
      // Загружаем новости для overview асинхронно (не блокируя UI) только если таб активен
      if (activeTab === 'overview') {
        setTimeout(() => loadOverviewNews(clanData.id), 0);
      }
      
    } catch (err: any) {
      console.error('Error loading clan data:', err);
      setError(err.message || 'Failed to load clan data');
      setLoading(false);
    } finally {
      setLoadingInProgress(false);
    }
  }, [id, user, loadingInProgress]);

  // Загрузка счетчиков для сайдбара Members (только при переходе на таб Members)
  const loadMembersCounters = useCallback(async () => {
    if (!clan || !user || countersLoaded) return;
    
    const isManager = user.id === clan.leaderId || user.id === clan.seniorOfficerId;
    if (!isManager) return;
    
    try {
      const [applications, invitations] = await Promise.allSettled([
        clanService.getClanApplications(clan.id),
        clanService.getClanInvitations(clan.id)
      ]);
      
      if (applications.status === 'fulfilled') {
        setApplicationsCount(applications.value?.length || 0);
      }
      if (invitations.status === 'fulfilled') {
        setInvitationsCount(invitations.value?.length || 0);
      }
      
      setCountersLoaded(true);
    } catch (error) {
      console.error('Error loading members counters:', error);
    }
  }, [clan, user, countersLoaded]);

  useEffect(() => {
    if (id) {
      loadClanData();
    }
  }, [id, loadClanData]);

  // Обновляем права управления при изменении пользователя или клана
  useEffect(() => {
    if (clan && user) {
      const isLeader = clan.leaderId === user.id;
      const isSeniorOfficer = clan.seniorOfficerId === user.id;
      const canManage = Boolean(isLeader || isSeniorOfficer);
      
      
      setCanManageMembers(canManage);
      setCanManageNews(canManage);
    }
  }, [clan, user]);

  // Ленивая загрузка данных при смене табов
  useEffect(() => {
    if (!clan) return;
    
    const loadTabSpecificData = async () => {
      switch (activeTab) {
        case 'members':
          // Загружаем участников и счетчики параллельно
          const loadPromises = [];
          
          // Загружаем участников только при первом переходе
          if (members.length === 0) {
            setAdditionalDataLoading(true);
            loadPromises.push(
              Promise.all([
                clanService.getClanMembers(clan.id),
                clanService.getClanRanks()
              ]).then(([membersData, ranksData]) => {
                setMembers(membersData || []);
                setClanRanks(ranksData || []);
              }).catch(error => {
                console.error('Error loading members:', error);
              }).finally(() => {
                setAdditionalDataLoading(false);
              })
            );
          }
          
          // Загружаем счетчики сразу при первом переходе на вкладку
          if (user && (user.id === clan.leaderId || user.id === clan.seniorOfficerId) && !countersLoaded) {
            loadPromises.push(loadMembersCounters());
          }
          
          // Выполняем все промисы параллельно
          if (loadPromises.length > 0) {
            Promise.allSettled(loadPromises);
          }
          break;
          
        case 'overview':
          // Загружаем заявки пользователя только при необходимости
          if (user && userApplications.length === 0) {
            try {
              const applications = await clanService.getMyApplications();
              setUserApplications(applications);
            } catch (error) {
              console.error('Error loading user applications:', error);
            }
          }
          break;
      }
    };

    loadTabSpecificData();
  }, [activeTab, clan, members.length, user, userApplications.length, applicationsCount, invitationsCount, loadMembersCounters]);

  // Функции для работы с новостями
  const loadOverviewNews = useCallback(async (clanId: string) => {
    try {
      // Не загружаем новости повторно если они уже загружены
      if (overviewNews.publicNews || overviewNews.internalNews) {
        return;
      }
      
      const newsData = await clanNewsService.getLatestNewsForOverview(clanId);
      setOverviewNews(newsData);
    } catch (error) {
      console.error('Error loading overview news:', error);
      setOverviewNews({ publicNews: null, internalNews: null });
    }
  }, [overviewNews.publicNews, overviewNews.internalNews]);

  const handleNewsClick = (news: ClanNews) => {
    setSelectedNews(news);
    setShowNewsModal(true);
  };

  const handleCloseNewsModal = () => {
    setShowNewsModal(false);
    setSelectedNews(null);
  };

  const handleCreateNews = () => {
    setEditingNews(undefined);
    setShowNewsForm(true);
  };

  const handleEditNews = (news: ClanNews) => {
    setEditingNews(news);
    setShowNewsForm(true);
  };

  const handleCloseNewsForm = () => {
    setShowNewsForm(false);
    setEditingNews(undefined);
  };

  const handleNewsSaved = (news: ClanNews) => {
    setShowNewsForm(false);
    setEditingNews(undefined);
    // Обновляем список новостей
    if (clan) {
      // Сбрасываем кеш новостей для их перезагрузки
      setOverviewNews({ publicNews: null, internalNews: null });
      loadOverviewNews(clan.id);
    }
  };

  const handleDeleteNews = async (news: ClanNews) => {
    if (!clan) return;

    const confirmed = await confirm({
      title: 'Delete News',
      message: `Are you sure you want to delete the news "${news.title}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await clanNewsService.deleteClanNews(clan.id, news.id);
      setOverviewNews({ publicNews: null, internalNews: null });
      loadOverviewNews(clan.id);
      showSuccess(
        'News Deleted',
        `News "${news.title}" has been deleted successfully.`,
        4000
      );
    } catch (error: any) {
      console.error('Error deleting news:', error);
      showError('Delete Failed', error.message || 'Failed to delete news');
    }
  };

  // Функция для обновления только заявок пользователя
  const refreshUserApplications = useCallback(async () => {
    if (!user) return;
    try {
      const applications = await clanService.getMyApplications();
      setUserApplications(applications);
    } catch (error) {
      console.warn('ClanDetailPage: Could not refresh user applications', error);
    }
  }, [user]);

  // Auto-refresh member data when switching to Members tab
  useEffect(() => {
    if (activeTab === 'members' && clan && user) {
      
      // Refresh members data
      clanService.getClanMembers(clan.id)
        .then(membersData => {
          setMembers(membersData || []);
        })
        .catch(error => {
          console.error('Error refreshing members:', error);
        });

      // Refresh counters if user has management permissions
      if (user.id === clan.leaderId || user.id === clan.seniorOfficerId) {
        loadMembersCounters();
      }

      // Refresh user applications
      refreshUserApplications();
    }
  }, [activeTab, clan, user, loadMembersCounters, refreshUserApplications]);

  // Auto-refresh achievements data when switching to Achievements tab
  useEffect(() => {
    if (activeTab === 'achievements' && clan) {
      setAchievementsRefreshTrigger(prev => prev + 1);
    }
  }, [activeTab, clan]);

  // Обработчик подачи заявки
  const handleApplyToClan = async () => {
    if (!clan || !user) return;
    
    try {
      setApplyLoading(true);
      
      // Проверяем, есть ли уже активная заявка в другой клан того же типа
      const myApplications = await clanService.getMyApplications();
      const activeApplications = myApplications.filter(app => 
        app.status === 'pending' && 
        app.clanType === clan.clanType
      );
      
      if (activeApplications.length > 0) {
        const existingClan = activeApplications[0];
        showError(
          'Application Limit Reached',
          `You already have a pending application to ${existingClan.clanName} for ${clan.clanType}. You can only have one active application per game type.`
        );
        return;
      }
      
      await clanService.applyToClan(clan.id);
      await refreshUserApplications(); // Обновляем только заявки
      showSuccess(
        'Application Submitted!',
        `Your application to join ${clan.name} has been submitted successfully.`,
        5000
      );
    } catch (err: any) {
      console.error('Error applying to clan:', err);
      showError('Application Failed', err.message || 'Failed to submit application');
    } finally {
      setApplyLoading(false);
    }
  };

  // Обработчик отмены заявки - открываем модальное окно
  const handleWithdrawApplication = () => {
    setShowWithdrawModal(true);
  };

  // Обработчики модального окна логотипа
  const handleLogoClick = () => {
    if (clan?.logoUrl) {
      setShowLogoModal(true);
    }
  };
  
  // Обработчики приглашения пользователей
  const handleInviteUser = () => {
    setShowInviteModal(true);
  };
  
  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
  };
  
  const handleUserInvited = () => {
    // Перезагружаем счетчики приглашений
    if (clan) {
      loadMembersCounters();
    }
  };

  const handleCloseLogoModal = () => {
    setShowLogoModal(false);
  };

  // Подтверждение отмены заявки
  const confirmWithdrawApplication = async () => {
    if (!clan || !user) return;
    
    try {
      setApplyLoading(true);
      setShowWithdrawModal(false);
      await clanService.withdrawApplication(clan.id, user.id);
      await refreshUserApplications(); // Обновляем заявки
      showSuccess(
        'Application Withdrawn',
        `Your application to join ${clan.name} has been withdrawn.`,
        4000
      );
    } catch (err: any) {
      console.error('Error withdrawing application:', err);
      showError('Withdraw Failed', err.message || 'Failed to withdraw application');
    } finally {
      setApplyLoading(false);
    }
  };


  const handleJoinClan = async () => {
    if (!clan || !user) return;
    
    try {
      setActionLoading(true);
      await clanService.joinClan(clan.id);
      // Перезагружаем только участников и разрешения
      const [membersData] = await Promise.all([
        clanService.getClanMembers(clan.id),
        refreshUserApplications()
      ]);
      setMembers(membersData || []);
      showSuccess(
        'Welcome to the Clan!',
        `You have successfully joined ${clan.name}. Welcome aboard!`,
        5000
      );
    } catch (err: any) {
      console.error('Error joining clan:', err);
      showError('Join Failed', err.message || 'Failed to join clan');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeaveClan = async () => {
    if (!clan || !user) return;

    try {
      setActionLoading(true);
      await clanService.leaveClan(clan.id);
      // Перезагружаем все данные клана
      await loadClanData();
      showSuccess(
        'Left Clan',
        `You have successfully left ${clan.name}.`,
        4000
      );
    } catch (err: any) {
      console.error('Error leaving clan:', err);
      showError('Leave Failed', err.message || 'Failed to leave clan');
    } finally {
      setActionLoading(false);
    }
  };

  const isUserInClan = () => {
    return !!(clan && user && clan.memberIds?.includes(user.id));
  };

  // Проверяет состоит ли пользователь в каком-либо клане данного типа игры
  const isUserInAnyGameTypeClan = () => {
    if (!user || !clan) return false;
    
    switch (clan.clanType) {
      case 'airsoft':
        return !!(user.airsoftClanId && user.airsoftClanId.trim() !== '');
      case 'paintball':  
        return !!(user.paintballClanId && user.paintballClanId.trim() !== '');
      default:
        return false;
    }
  };

  // Проверяет есть ли у пользователя активная заявка в этот клан
  const hasActiveApplication = () => {
    if (!user || !clan || !userApplications) return false;
    return userApplications.some(app => 
      app.clanId === clan.id && 
      app.clanType === clan.clanType && 
      app.status === 'pending'
    );
  };

  const getUserClanRole = () => {
    if (!clan || !user) return null;
    
    const role = clan.leaderId === user.id ? 'Leader' : 
                 clan.seniorOfficerId === user.id ? 'Officer' : 
                 'Member';
    
    
    return role;
  };

  const getMemberRole = (memberId: string) => {
    if (!clan) return 'Member';
    if (clan.leaderId === memberId) return 'Leader';
    if (clan.seniorOfficerId === memberId) return 'Officer';
    return 'Member';
  };

  const getRankProgress = () => {
    if (!clan || clanRanks.length === 0) return 0;
    return clanService.getRankProgress(clan.points, clan.rank, clanRanks);
  };

  const getPointsToNextRank = () => {
    if (!clan || clanRanks.length === 0) return 0;
    return clanService.getPointsToNextRank(clan.points, clan.rank, clanRanks);
  };


  const handleRemoveMember = async (userId: string) => {
    if (!clan || !user) return;
    
    try {
      setRemovingMember(userId);
      await clanService.removeMemberFromClan(clan.id, userId);
      await loadClanData(); // Перезагружаем данные
      setMemberToRemove(null);
      
      const removedMember = members.find(m => m.id === userId);
      showSuccess(
        'Member Removed',
        `${removedMember?.name || 'Member'} has been removed from the clan.`,
        4000
      );
    } catch (err: any) {
      console.error('Error removing member:', err);
      showError('Remove Failed', err.message || 'Failed to remove member');
    } finally {
      setRemovingMember(null);
    }
  };

  const confirmRemoveMember = (userId: string) => {
    setMemberToRemove(userId);
  };

  const cancelRemoveMember = () => {
    setMemberToRemove(null);
  };

  const handleAssignSeniorOfficer = async (userId: string) => {
    if (!clan || !user) return;
    
    try {
      setAssigningOfficer(userId);
      await clanService.assignSeniorOfficer(clan.id, userId);
      await loadClanData(); // Перезагружаем данные
      
      const assignedMember = members.find(m => m.id === userId);
      showSuccess(
        'Senior Officer Assigned',
        `${assignedMember?.name || 'Member'} has been assigned as senior officer.`,
        4000
      );
    } catch (err: any) {
      console.error('Error assigning senior officer:', err);
      showError('Assignment Failed', err.message || 'Failed to assign senior officer');
    } finally {
      setAssigningOfficer(null);
    }
  };

  const handleRemoveSeniorOfficer = async () => {
    if (!clan || !user) return;
    
    try {
      setRemovingOfficer(true);
      await clanService.removeSeniorOfficer(clan.id);
      await loadClanData();
      showSuccess('Senior Officer Removed', 'Senior officer removed successfully');
    } catch (err: any) {
      showError('Remove Failed', err.message || 'Failed to remove senior officer');
    } finally {
      setRemovingOfficer(false);
    }
  };

  // Обработчики для формы редактирования клана
  const handleEditFormChange = (field: string, value: string | string[] | File | null) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStateChange = (state: string) => {
    setEditForm(prev => ({
      ...prev,
      state,
      // Очищаем поля при изменении штата
      primaryBase: '',
      secondaryBases: []
    }));
  };

  const handlePrimaryBaseChange = (primaryBase: string) => {
    setEditForm(prev => ({
      ...prev,
      primaryBase,
      // Удаляем из второстепенных баз, если была там
      secondaryBases: prev.secondaryBases.filter(base => base !== primaryBase)
    }));
  };

  const handleSecondaryBasesChange = (secondaryBases: string[]) => {
    setEditForm(prev => ({
      ...prev,
      secondaryBases
    }));
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Проверяем размер файла (макс. 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('File Too Large', 'Logo file must be less than 5MB');
        return;
      }
      
      // Проверяем тип файла
      if (!file.type.startsWith('image/')) {
        showError('Invalid File Type', 'Please select an image file');
        return;
      }
      
      setEditForm(prev => ({
        ...prev,
        logoFile: file
      }));
    }
  };

  const handleMemberClick = (memberId: string) => {
    navigate(`/profile/${memberId}`);
  };

  const handleUpdateClan = async () => {
    if (!clan || !user) return;
    
    // Валидация
    if (!editForm.name.trim() || !editForm.tag.trim() || !editForm.description.trim()) {
      showError('Validation Error', 'All fields are required');
      return;
    }

    if (!editForm.state) {
      showError('Validation Error', 'State is required');
      return;
    }

    if (editForm.secondaryBases.length > 10) {
      showError('Validation Error', 'Maximum 10 secondary bases allowed');
      return;
    }

    // Проверяем, что основная база не дублируется во второстепенных
    if (editForm.primaryBase && editForm.secondaryBases.includes(editForm.primaryBase)) {
      showError('Validation Error', 'Primary base cannot be included in secondary bases');
      return;
    }
    
    try {
      setIsUpdating(true);
      
      const formData = new FormData();
      formData.append('name', editForm.name);
      formData.append('description', editForm.description);
      formData.append('tag', editForm.tag);
      formData.append('clanType', clan.clanType);
      formData.append('state', editForm.state);
      formData.append('primaryBase', editForm.primaryBase);
      
      // Добавляем второстепенные базы
      editForm.secondaryBases.forEach((base, index) => {
        formData.append(`secondaryBases[${index}]`, base);
      });
      
      if (editForm.logoFile) {
        formData.append('logoFile', editForm.logoFile);
      }
      
      await clanService.updateClan(clan.id, formData);
      await loadClanData();
      showSuccess('Clan Updated', 'Clan information updated successfully');
    } catch (err: any) {
      showError('Update Failed', err.message || 'Failed to update clan information');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading clan data...</LoadingSpinner>
      </Container>
    );
  }

  if (error || !clan) {
    return (
      <Container>
        <ErrorMessage>{error || 'Clan not found'}</ErrorMessage>
      </Container>
    );
  }

  const status = clanService.getClanStatus(clan);
  const efficiency = clanService.getClanEfficiencyRating(clan);
  const rankProgress = getRankProgress();
  const pointsToNext = getPointsToNextRank();

  const isAllStatesSelected = editForm.state === ALL_STATES_OPTION;
  const excludedFields = editForm.primaryBase ? [editForm.primaryBase] : [];

  return (
    <PageBackground $shadow={isShadowGrid}>
      {isNightOpsMode && <DottedOverlay />}
      {isModernWarfare && <ModernBackground />}
      <Container>
        <ShadowGridWrapper $enabled={isShadowGrid}>
          <Content>
        <NotificationContainer 
          notifications={notifications} 
          onRemove={removeNotification} 
        />
        
        <PageHeader>
          <Title>{clan.name}</Title>
          <Subtitle>Elite tactical {clan.clanType} unit</Subtitle>
        </PageHeader>
        
        <Header>
        <ClanInfo>
          {clan.logoUrl && (
            <ClanLogo src={clan.logoUrl} alt={`Logo ${clan.name}`} onClick={handleLogoClick} />
          )}
          <ClanTextInfo>
            <ClanTag>{clan.tag}</ClanTag>
          </ClanTextInfo>
        </ClanInfo>
        {user && (
          <ActionButtons>
            {isUserInClan() ? (
              <>
                {getUserClanRole() !== 'Leader' && (
                  <LeaveButton onClick={handleLeaveClan} disabled={actionLoading}>
                    {actionLoading ? 'Leaving...' : 'Leave Clan'}
                  </LeaveButton>
                )}
              </>
            ) : isUserInAnyGameTypeClan() ? (
              // Пользователь уже в другом клане этого типа игры - ничего не показываем
              <></>
            ) : (
              <>
                {hasActiveApplication() ? (
                  <WithdrawButton onClick={handleWithdrawApplication} disabled={applyLoading}>
                    {applyLoading ? 'Withdrawing...' : 'Withdraw Application'}
                  </WithdrawButton>
                ) : (
                  <ApplyButton onClick={handleApplyToClan} disabled={applyLoading}>
                    {applyLoading ? 'Applying...' : 'Apply to Join'}
                  </ApplyButton>
                )}
              </>
            )}
          </ActionButtons>
        )}
      </Header>

      <Tabs>
        <Tab 
          active={activeTab === 'overview'} 
          onClick={() => setActiveTab('overview')}
        >
          <TabText>
            <span className="full-text">Overview</span>
            <span className="short-text">Info</span>
          </TabText>
        </Tab>
        <Tab 
          active={activeTab === 'news'} 
          onClick={() => setActiveTab('news')}
        >
          <TabText>
            <span className="full-text">News</span>
            <span className="short-text">News</span>
          </TabText>
        </Tab>
        <Tab 
          active={activeTab === 'members'} 
          onClick={() => setActiveTab('members')}
        >
          <TabText>
            <span className="full-text">Members</span>
            <span className="short-text">Members</span>
          </TabText>
          <TabCount>({clan.memberIds?.length || 0})</TabCount>
        </Tab>
        <Tab 
          active={activeTab === 'achievements'} 
          onClick={() => setActiveTab('achievements')}
        >
          <TabText>
            <span className="full-text">Achievements</span>
            <span className="short-text">Awards</span>
          </TabText>
          <TabCount>({clan.achievements?.length || 0})</TabCount>
        </Tab>
        {/* Вкладка Settings только для лидера клана */}
        {user && clan && clan.leaderId === user.id && (
          <Tab 
            active={activeTab === 'settings'} 
            onClick={() => setActiveTab('settings')}
          >
            <TabText>
              <span className="full-text">Settings</span>
              <span className="short-text">Settings</span>
            </TabText>
          </Tab>
        )}
      </Tabs>

      <TabContent>
        {activeTab === 'overview' && (
          <OverviewTab>
            <ClanStats>
              <StatCard>
                <StatTitle>Clan Rank</StatTitle>
                <RankInfo>
                  <RankBadge color={clanService.getRankColor(clan.rank)}>
                    {clan.rankName}
                  </RankBadge>
                  <RankLevel>Level {clan.rank}</RankLevel>
                </RankInfo>
                {pointsToNext > 0 && (
                  <RankProgress>
                    <ProgressBar>
                      <ProgressFill width={rankProgress} />
                    </ProgressBar>
                    <ProgressText>
                      {clanService.formatNumber(pointsToNext)} points to next rank
                    </ProgressText>
                  </RankProgress>
                )}
              </StatCard>

              <StatCard>
                <StatTitle>Main Information</StatTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>Members:</InfoLabel>
                    <InfoValue>{clan.memberIds?.length || 0}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Points:</InfoLabel>
                    <InfoValue>{clanService.formatNumber(clan.points || 0)}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Missions:</InfoLabel>
                    <InfoValue>{clan.totalMissions || 0}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Win Rate:</InfoLabel>
                    <InfoValue>{(clan.winRate || 0).toFixed(1)}%</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </StatCard>

              <StatCard>
                <StatTitle>Status and Rating</StatTitle>
                <BadgeGrid>
                  <StatusBadge color={status.color}>
                    {status.label}
                  </StatusBadge>
                  <EfficiencyBadge color={efficiency.color}>
                    {efficiency.label} ({efficiency.rating}%)
                  </EfficiencyBadge>
                </BadgeGrid>
              </StatCard>
            </ClanStats>

            {/* Секция статистики игр */}
            <StatCard>
              <StatTitle>Game Statistics</StatTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Total Games:</InfoLabel>
                  <InfoValue>{clan.clanGameStatistics?.totalGames || 0}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Total Wins:</InfoLabel>
                  <InfoValue>{clan.clanGameStatistics?.totalWins || 0}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Airsoft Games:</InfoLabel>
                  <InfoValue>{clan.clanGameStatistics?.totalAirsoftGames || 0}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Paintball Games:</InfoLabel>
                  <InfoValue>{clan.clanGameStatistics?.totalPaintballGames || 0}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Speedball Games:</InfoLabel>
                  <InfoValue>{clan.clanGameStatistics?.totalSpeedballGames || 0}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Win Rate:</InfoLabel>
                  <InfoValue>{(clan.clanGameStatistics?.winRate || 0).toFixed(1)}%</InfoValue>
                </InfoItem>
              </InfoGrid>
            </StatCard>

            <LocationInfo>
              <LocationTitle>Location Information</LocationTitle>
              <LocationGrid>
                <LocationItem>
                  <LocationLabel>State:</LocationLabel>
                  <LocationValue>{clan.state || 'Not specified'}</LocationValue>
                </LocationItem>
                {clan.primaryBase && (
                  <LocationItem>
                    <LocationLabel>Primary Base:</LocationLabel>
                    <LocationValue>{clan.primaryBase}</LocationValue>
                  </LocationItem>
                )}
                {clan.secondaryBases && clan.secondaryBases.length > 0 && (
                  <LocationSecondaryBases>
                    <LocationLabel>Secondary Bases:</LocationLabel>
                    <SecondaryBasesList>
                      {clan.secondaryBases.map((base, index) => (
                        <SecondaryBase key={index}>{base}</SecondaryBase>
                      ))}
                    </SecondaryBasesList>
                  </LocationSecondaryBases>
                )}
              </LocationGrid>
            </LocationInfo>

            <ClanDescription>
              <DescriptionTitle>Clan Description</DescriptionTitle>
              <DescriptionText>{clan.description}</DescriptionText>
            </ClanDescription>

            <ClanLeaderInfo>
              <LeaderTitle>Clan Leadership</LeaderTitle>
              <LeaderName as={Link} to={`/profile/${clan.leaderId}`}>{clan.leaderName}</LeaderName>
              <LeaderRole>Leader</LeaderRole>
              
              {clan.seniorOfficerId && clan.seniorOfficerName ? (
                <>
                  <SeniorOfficerName as={Link} to={`/profile/${clan.seniorOfficerId}`}>{clan.seniorOfficerName}</SeniorOfficerName>
                  <SeniorOfficerRole>Senior Officer</SeniorOfficerRole>
                  {getUserClanRole() === 'Leader' && (
                    <RemoveSeniorOfficerButton 
                      onClick={handleRemoveSeniorOfficer}
                      disabled={removingOfficer}
                    >
                      {removingOfficer ? 'Removing...' : 'Remove Senior Officer'}
                    </RemoveSeniorOfficerButton>
                  )}
                </>
              ) : (
                <div style={{ color: '#888', fontSize: '0.9rem', marginTop: '10px' }}>
                  No senior officer assigned
                  {getUserClanRole() === 'Leader' && (
                    <div style={{ marginTop: '10px' }}>
                      <small style={{ color: '#666' }}>
                        Go to Members tab to assign a senior officer
                      </small>
                    </div>
                  )}
                </div>
              )}
              
              <LeaderSince>
                Created: {new Date(clan.createdAt).toLocaleDateString('en-US')}
              </LeaderSince>
            </ClanLeaderInfo>

            {/* Секция достижений */}
            <AchievementsSection>
              <AchievementsSectionHeader>
                <AchievementsSectionTitle>Recent Achievements</AchievementsSectionTitle>
              </AchievementsSectionHeader>
              <ClanAchievements 
                clanId={clan.id} 
                gameType={clan.clanType as 'airsoft' | 'paintball' | 'all'} 
                refreshTrigger={achievementsRefreshTrigger}
              />
            </AchievementsSection>

            {/* Секция новостей */}
            <NewsSection>
              <NewsSectionHeader>
                <NewsSectionTitle>Latest News</NewsSectionTitle>
                {canManageNews && (
                  <CreateNewsButton onClick={handleCreateNews}>
                    Create News
                  </CreateNewsButton>
                )}
              </NewsSectionHeader>
              
              <NewsGrid>
                {overviewNews.publicNews && (
                  <NewsCard 
                    onClick={() => handleNewsClick(overviewNews.publicNews!)}
                    type="Public"
                  >
                    <NewsCardHeader>
                      <NewsCardTitle>{overviewNews.publicNews.title}</NewsCardTitle>
                      <NewsCardBadge type="Public">Public</NewsCardBadge>
                    </NewsCardHeader>
                    <NewsCardContent>
                      {clanNewsService.truncateText(overviewNews.publicNews.content, 120)}
                    </NewsCardContent>
                    <NewsCardFooter>
                      <NewsCardAuthor>By: {overviewNews.publicNews.authorName}</NewsCardAuthor>
                      <NewsCardDate>{clanNewsService.formatDate(overviewNews.publicNews.createdAt)}</NewsCardDate>
                    </NewsCardFooter>
                    {canManageNews && (
                      <NewsCardActions>
                        <EditNewsButton onClick={(e) => {
                          e.stopPropagation();
                          handleEditNews(overviewNews.publicNews!);
                        }}>
                          Edit
                        </EditNewsButton>
                        <DeleteNewsButton onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNews(overviewNews.publicNews!);
                        }}>
                          Delete
                        </DeleteNewsButton>
                      </NewsCardActions>
                    )}
                  </NewsCard>
                )}
                
                {overviewNews.internalNews && isUserInClan() && (
                  <NewsCard 
                    onClick={() => handleNewsClick(overviewNews.internalNews!)}
                    type="Internal"
                  >
                    <NewsCardHeader>
                      <NewsCardTitle>{overviewNews.internalNews.title}</NewsCardTitle>
                      <NewsCardBadge type="Internal">Internal</NewsCardBadge>
                    </NewsCardHeader>
                    <NewsCardContent>
                      {clanNewsService.truncateText(overviewNews.internalNews.content, 120)}
                    </NewsCardContent>
                    <NewsCardFooter>
                      <NewsCardAuthor>By: {overviewNews.internalNews.authorName}</NewsCardAuthor>
                      <NewsCardDate>{clanNewsService.formatDate(overviewNews.internalNews.createdAt)}</NewsCardDate>
                    </NewsCardFooter>
                    {canManageNews && (
                      <NewsCardActions>
                        <EditNewsButton onClick={(e) => {
                          e.stopPropagation();
                          handleEditNews(overviewNews.internalNews!);
                        }}>
                          Edit
                        </EditNewsButton>
                        <DeleteNewsButton onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNews(overviewNews.internalNews!);
                        }}>
                          Delete
                        </DeleteNewsButton>
                      </NewsCardActions>
                    )}
                  </NewsCard>
                )}
                
                {!overviewNews.publicNews && !overviewNews.internalNews && (
                  <NoNewsMessage>
                    No news available yet.
                    {canManageNews && (
                      <span>
                        <br />
                        <CreateNewsLink onClick={handleCreateNews}>
                          Create the first news post
                        </CreateNewsLink>
                      </span>
                    )}
                  </NoNewsMessage>
                )}
              </NewsGrid>
            </NewsSection>

            {/* Секция игр клана */}
            <StatCard>
              <StatTitle>Recent Clan Missions</StatTitle>
              <ClanGamesSection
                clanId={clan.id}
                clanName={clan.name}
                supportedGameTypes={[clan.clanType]}
                showFilters={true}
                showPagination={true}
              />
            </StatCard>
          </OverviewTab>
        )}

        {activeTab === 'members' && (
          <MembersTab>
            <MembersLayoutContainer>
              {/* Sidebar Navigation */}
              <MembersSidebar>
                <SidebarSection>
                  <SidebarSectionTitle>Management</SidebarSectionTitle>
                  
                  <SidebarNavItem 
                    active={memberSubTab === 'list'}
                    onClick={() => setMemberSubTab('list')}
                  >
                    <NavItemContent>
                      <NavItemIcon>👥</NavItemIcon>
                      <NavItemText>Members</NavItemText>
                    </NavItemContent>
                    <NavBadge active={memberSubTab === 'list'}>
                      {clan.memberIds?.length || 0}
                    </NavBadge>
                  </SidebarNavItem>
                  
                  {user && clan && (user.id === clan.leaderId || user.id === clan.seniorOfficerId) && (
                    <>
                      <SidebarNavItem 
                        active={memberSubTab === 'applications'}
                        onClick={() => setMemberSubTab('applications')}
                      >
                        <NavItemContent>
                          <NavItemIcon>📝</NavItemIcon>
                          <NavItemText>Applications</NavItemText>
                        </NavItemContent>
                        <NavBadge active={memberSubTab === 'applications'}>
                          {applicationsCount}
                        </NavBadge>
                      </SidebarNavItem>
                      
                      <SidebarNavItem 
                        active={memberSubTab === 'invitations'}
                        onClick={() => setMemberSubTab('invitations')}
                      >
                        <NavItemContent>
                          <NavItemIcon>✉️</NavItemIcon>
                          <NavItemText>Sent Invitations</NavItemText>
                        </NavItemContent>
                        <NavBadge active={memberSubTab === 'invitations'}>
                          {invitationsCount}
                        </NavBadge>
                      </SidebarNavItem>
                    </>
                  )}
                </SidebarSection>

                {user && clan && (user.id === clan.leaderId || user.id === clan.seniorOfficerId) && (
                  <QuickActions>
                    <SidebarSectionTitle>Quick Actions</SidebarSectionTitle>
                    <QuickActionButton onClick={handleInviteUser}>
                      ➕ Invite Member
                    </QuickActionButton>
                  </QuickActions>
                )}
              </MembersSidebar>

              {/* Content Area */}
              <MembersContent>
                {/* Members List Sub-tab */}
                {memberSubTab === 'list' && (
                  <>
                    {additionalDataLoading && members.length === 0 ? (
                      <LoadingContainer>
                        <LoadingSpinner>Loading members...</LoadingSpinner>
                      </LoadingContainer>
                    ) : (
                      <MembersGrid>
                        {members && members.map((member) => (
                        <MemberCard key={member.id} onClick={() => handleMemberClick(member.id)}>
                          <MemberHeader>
                            <MemberName>{member.name}</MemberName>
                            <MemberRole color={CLAN_ROLES[getMemberRole(member.id)]?.color || '#4CAF50'}>
                              {CLAN_ROLES[getMemberRole(member.id)]?.label || getMemberRole(member.id)}
                            </MemberRole>
                          </MemberHeader>
                         
                          <MemberStats>
                            <StatRow>
                              <StatLabel>Missions:</StatLabel>
                              <StatValue>
                                {clan?.clanType === 'airsoft' ? (member.gameStatistics?.totalAirsoftGames || 0) :
                                 clan?.clanType === 'paintball' ? (member.gameStatistics?.totalPaintballGames || 0) :
                                 clan?.clanType === 'speedball' ? (member.gameStatistics?.totalSpeedballGames || 0) :
                                 (member.gameStatistics?.totalGames || 0)}
                              </StatValue>
                            </StatRow>
                            <StatRow>
                              <StatLabel>Wins:</StatLabel>
                              <StatValue>
                                {clan?.clanType === 'airsoft' ? (member.gameStatistics?.airsoftStatistics?.totalWins || 0) :
                                 clan?.clanType === 'paintball' ? (member.gameStatistics?.paintballStatistics?.totalWins || 0) :
                                 clan?.clanType === 'speedball' ? (member.gameStatistics?.speedballStatistics?.totalWins || 0) :
                                 (member.gameStatistics?.totalWins || 0)}
                              </StatValue>
                            </StatRow>
                            <StatRow>
                              <StatLabel>Win Rate:</StatLabel>
                              <StatValue>
                                {clan?.clanType === 'airsoft' ? ((member.gameStatistics?.airsoftStatistics?.winRate || 0).toFixed(1) + '%') :
                                 clan?.clanType === 'paintball' ? ((member.gameStatistics?.paintballStatistics?.winRate || 0).toFixed(1) + '%') :
                                 clan?.clanType === 'speedball' ? ((member.gameStatistics?.speedballStatistics?.winRate || 0).toFixed(1) + '%') :
                                 ((member.gameStatistics?.winRate || 0).toFixed(1) + '%')}
                              </StatValue>
                            </StatRow>
                            <StatRow>
                              <StatLabel>Achievements:</StatLabel>
                              <StatValue>{member.achievements?.length || 0}</StatValue>
                            </StatRow>
                          </MemberStats>

                          <FactionRanks>
                            <FactionRank>
                              <FactionLabel>People:</FactionLabel>
                              <FactionRankName>{member.humanFactionRankName || 'Not specified'}</FactionRankName>
                            </FactionRank>
                            <FactionRank>
                              <FactionLabel>Aliens:</FactionLabel>
                              <FactionRankName>{member.alienFactionRankName || 'Not specified'}</FactionRankName>
                            </FactionRank>
                          </FactionRanks>

                          <MemberSince>
                            Joined clan on: {new Date(member.createdAt).toLocaleDateString('en-US')}
                          </MemberSince>

                          <ProfileHint>
                            Click to view profile
                          </ProfileHint>

                          {/* Управление участниками (только для лидера и старшего офицера) */}
                          {canManageMembers && member.id !== user?.id && (
                            <MemberActions onClick={(e) => e.stopPropagation()}>
                              {/* Назначение старшего офицера (только для лидера) */}
                              {getUserClanRole() === 'Leader' && 
                               member.id !== clan.leaderId && 
                               member.id !== clan.seniorOfficerId && (
                                <AssignOfficerButton 
                                  onClick={() => handleAssignSeniorOfficer(member.id)}
                                  disabled={assigningOfficer === member.id}
                                >
                                  {assigningOfficer === member.id ? 'Assigning...' : 'Make Senior Officer'}
                                </AssignOfficerButton>
                              )}
                              
                              {/* Снятие старшего офицера (только для лидера) */}
                              {getUserClanRole() === 'Leader' && 
                               member.id === clan.seniorOfficerId && (
                                <RemoveOfficerButton 
                                  onClick={handleRemoveSeniorOfficer}
                                  disabled={removingOfficer}
                                >
                                  {removingOfficer ? 'Removing...' : 'Remove Officer'}
                                </RemoveOfficerButton>
                              )}
                              
                              {/* Удаление участника */}
                              {memberToRemove === member.id ? (
                                <ConfirmActions>
                                  <ConfirmText>Remove {member.name}?</ConfirmText>
                                  <ConfirmButton 
                                    onClick={() => handleRemoveMember(member.id)}
                                    disabled={removingMember === member.id}
                                  >
                                    {removingMember === member.id ? 'Removing...' : 'Yes'}
                                  </ConfirmButton>
                                  <CancelButton onClick={cancelRemoveMember}>No</CancelButton>
                                </ConfirmActions>
                              ) : (
                                <RemoveButton onClick={() => confirmRemoveMember(member.id)}>
                                  Remove Member
                                </RemoveButton>
                              )}
                            </MemberActions>
                          )}
                        </MemberCard>
                        ))}
                      </MembersGrid>
                    )}
                    {!additionalDataLoading && members.length === 0 && (
                      <EmptyMembers>
                        <EmptyTitle>No members found</EmptyTitle>
                        <EmptyDescription>
                          No members in the clan yet.
                        </EmptyDescription>
                      </EmptyMembers>
                    )}
                  </>
                )}

                {/* Applications Sub-tab */}
                {memberSubTab === 'applications' && (
                  <ClanApplicationsTab 
                    clanId={clan.id} 
                    canManage={user ? (user.id === clan.leaderId || user.id === clan.seniorOfficerId) : false} 
                    isActive={memberSubTab === 'applications'} 
                  />
                )}

                {/* Invitations Sub-tab */}
                {memberSubTab === 'invitations' && (
                  <ClanInvitationsTab 
                    clanId={clan.id} 
                    canManage={user ? (user.id === clan.leaderId || user.id === clan.seniorOfficerId) : false} 
                    isActive={memberSubTab === 'invitations'}
                  />
                )}
              </MembersContent>
            </MembersLayoutContainer>
          </MembersTab>
        )}

        {activeTab === 'achievements' && (
          <ClanAchievements 
            clanId={clan.id} 
            gameType={clan.clanType as 'airsoft' | 'paintball' | 'all'} 
            refreshTrigger={achievementsRefreshTrigger}
          />
        )}

        {/* Вкладка News */}
        {activeTab === 'news' && (
          <ClanNewsTab
            clanId={clan.id}
            canManageNews={canManageNews}
            isUserInClan={isUserInClan()}
            onNewsClick={handleNewsClick}
          />
        )}

        {/* Вкладка Settings только для лидера клана */}
        {activeTab === 'settings' && user && clan && clan.leaderId === user.id && (
          <SettingsTab>
            <SettingsTitle>Clan Settings</SettingsTitle>
            <SettingsDescription>
              Update your clan information. Changes will be visible to all members.
            </SettingsDescription>
            
            <SettingsForm>
              <FormGroup>
                <FormLabel>Clan Name</FormLabel>
                <FormInput
                  type="text"
                  value={editForm.name}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                  placeholder="Enter clan name"
                  maxLength={50}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Clan Tag</FormLabel>
                <FormInput
                  type="text"
                  value={editForm.tag}
                  onChange={(e) => handleEditFormChange('tag', e.target.value)}
                  placeholder="Enter clan tag (e.g., NOVA)"
                  maxLength={10}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>State *</FormLabel>
                <StateSelector
                  value={editForm.state}
                  onChange={handleStateChange}
                  placeholder="Select your clan's state"
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Primary Base</FormLabel>
                <GameFieldSelector
                  value={editForm.primaryBase}
                  onChange={handlePrimaryBaseChange}
                  clanType={clan.clanType}
                  state={editForm.state}
                  placeholder="Select primary game field"
                  disabled={isAllStatesSelected}
                  excludeFields={editForm.secondaryBases}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Secondary Bases</FormLabel>
                <MultiGameFieldSelector
                  value={editForm.secondaryBases}
                  onChange={handleSecondaryBasesChange}
                  clanType={clan.clanType}
                  placeholder="Search and select secondary game fields"
                  disabled={isAllStatesSelected}
                  maxSelections={10}
                  excludeFields={excludedFields}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Description</FormLabel>
                <FormTextarea
                  value={editForm.description}
                  onChange={(e) => handleEditFormChange('description', e.target.value)}
                  placeholder="Enter clan description"
                  maxLength={500}
                  rows={4}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Clan Logo</FormLabel>
                <LogoSection>
                  {clan.logoUrl && (
                    <CurrentLogo>
                      <LogoPreview src={clan.logoUrl} alt="Current logo" />
                      <LogoLabel>Current Logo</LogoLabel>
                    </CurrentLogo>
                  )}
                  <FileInputWrapper>
                    <FileInput
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      id="logo-upload"
                    />
                    <FileInputLabel htmlFor="logo-upload">
                      {editForm.logoFile ? editForm.logoFile.name : 'Choose new logo'}
                    </FileInputLabel>
                    <FileInputHint>
                      Max 5MB. Supported formats: JPEG, PNG, GIF, WebP
                    </FileInputHint>
                  </FileInputWrapper>
                </LogoSection>
              </FormGroup>

              <FormActions>
                <SaveButton onClick={handleUpdateClan} disabled={isUpdating}>
                  {isUpdating ? 'Updating...' : 'Save Changes'}
                </SaveButton>
                <CancelButton 
                  onClick={() => {
                    setEditForm({
                      name: clan.name || '',
                      description: clan.description || '',
                      tag: clan.tag || '',
                      state: clan.state || '',
                      primaryBase: clan.primaryBase || '',
                      secondaryBases: clan.secondaryBases || [],
                      logoFile: null
                    });
                  }}
                  disabled={isUpdating}
                >
                  Reset
                </CancelButton>
              </FormActions>
            </SettingsForm>
          </SettingsTab>
        )}
      </TabContent>
      

      {/* Модальное окно для просмотра новости */}
      {selectedNews && (
        <ClanNewsModal
          news={selectedNews}
          isOpen={showNewsModal}
          onClose={handleCloseNewsModal}
        />
      )}

      {/* Модальное окно для создания/редактирования новости */}
      {clan && (
        <ClanNewsForm
          clanId={clan.id}
          news={editingNews}
          isOpen={showNewsForm}
          onClose={handleCloseNewsForm}
          onSave={handleNewsSaved}
        />
      )}

      {/* Logo Modal */}
      <ImageModal
        isOpen={showLogoModal}
        imageUrl={clan?.logoUrl || ''}
        imageAlt={`Logo ${clan?.name || 'Clan'}`}
        title={`${clan?.name || 'Clan'} Logo`}
        onClose={handleCloseLogoModal}
      />

      {/* Withdraw Application Modal */}
      {showWithdrawModal && (
        <ModalOverlay onClick={() => setShowWithdrawModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Withdraw Application</ModalTitle>
              <CloseButton onClick={() => setShowWithdrawModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <WarningIcon>⚠️</WarningIcon>
              <ModalText>
                Are you sure you want to withdraw your application to join <strong>{clan?.name}</strong>?
              </ModalText>
              <ModalSubtext>
                This action cannot be undone. You will need to reapply if you change your mind.
              </ModalSubtext>
            </ModalBody>
            <ModalActions>
              <CancelModalButton onClick={() => setShowWithdrawModal(false)}>
                Keep Application
              </CancelModalButton>
              <WithdrawModalButton 
                onClick={confirmWithdrawApplication}
                disabled={applyLoading}
              >
                {applyLoading ? 'Withdrawing...' : 'Withdraw Application'}
              </WithdrawModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Invite User Modal */}
      {clan && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={handleCloseInviteModal}
          clanId={clan.id}
          clanName={clan.name}
          onUserInvited={handleUserInvited}
        />
      )}
      <ConfirmModal {...modalProps} />
          </Content>
        </ShadowGridWrapper>
      </Container>
    </PageBackground>
  );
};

const PageBackground = styled.div<{ $shadow?: boolean }>`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  padding: 20px;
  padding-top: 100px;
  position: relative;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding: 10px;
    padding-top: 90px;
  }

  @media (max-width: 480px) {
    padding: 70px 10px 20px 10px;
  }
`;

const DottedOverlay = styled.div`
  display: none;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 3;
  
  @media (max-width: 1240px) {
    max-width: 95%;
  }
`;

const Content = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => {
    if (theme.name === 'shadowGrid') return "'Courier New', monospace";
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return "'Rajdhani', sans-serif";
  }};
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
  min-width: 0;
  overflow: hidden;
  text-align: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    margin-bottom: 25px;
  }

  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 20px;
  }
`;

const BackButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? 'rgba(5, 7, 10, 0.8)' 
    : 'var(--legacy-card-bg)'};
  border: 2px solid ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.secondary 
    : '#00d4ff'};
  color: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.secondary 
    : '#00d4ff'};
  padding: 10px 20px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: all 0.3s ease;
  backdrop-filter: none;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : 'normal'};

  &:hover {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? 'rgba(5, 217, 232, 0.1)' 
      : 'rgba(0, 212, 255, 0.1)'};
    border-color: ${({ theme }) => theme.name === 'modernWarfare' 
      ? theme.colors.secondary 
      : '#00d4ff'};
    box-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
      ? `0 0 15px ${theme.colors.secondary}40` 
      : 'none'};
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
    order: 3;
  }
`;

const ClanInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
  justify-content: center;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    order: 1;
  }
`;

const ClanLogo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  object-fit: cover;
  border: 2px solid ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    border-color: ${({ theme }) => theme.name === 'modernWarfare' ? '#fff' : '#40e0ff'};
    box-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
      ? `0 0 20px ${theme.colors.secondary}60` 
      : '0 4px 15px rgba(0, 212, 255, 0.4)'};
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
  }
`;

const ClanTextInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  max-width: 100%;

  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

const ClanName = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  margin: 0;
  line-height: 1.3;
  word-wrap: break-word;
  transform: translateY(1px);
  text-align: center;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  text-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
    ? `0 0 20px ${theme.colors.secondary}60` 
    : 'none'};

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 10px;
    transform: none;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const ClanTag = styled.span`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? `rgba(5, 217, 232, 0.1)` 
    : 'rgba(0, 212, 255, 0.2)'};
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-size: 1.2rem;
  font-weight: bold;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
  display: inline-block;
  white-space: normal;
  text-align: center;
  border: ${({ theme }) => theme.name === 'modernWarfare' ? `1px solid ${theme.colors.secondary}40` : 'none'};

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 1.3rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    padding: 8px 16px;
  }

  @media (max-width: 360px) {
    font-size: 1rem;
    padding: 6px 12px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: bold;
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
    ? `0 0 20px ${theme.colors.secondary}60` 
    : '0 0 15px rgba(0, 212, 255, 0.7)'};
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 15px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#8bb8d8'};
  margin: 0;
  letter-spacing: 0.5px;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.4;
    padding: 0 10px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    order: 2;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;


const JoinButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.primary 
    : 'linear-gradient(135deg, #4CAF50, #45a049)'};
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : 'normal'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? theme.colors.primaryDark 
      : 'linear-gradient(135deg, #45a049, #3d8b40)'};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
      ? `0 0 15px ${theme.colors.primary}60` 
      : 'none'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 15px 30px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    font-size: 1rem;
  }
`;

const ApplyButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.secondary 
    : 'linear-gradient(135deg, #007bff, #0056b3)'};
  border: none;
  color: ${({ theme }) => theme.name === 'modernWarfare' ? '#000' : 'white'};
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : 'normal'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? '#04b4c0' 
      : 'linear-gradient(135deg, #0056b3, #004085)'};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
      ? `0 0 15px ${theme.colors.secondary}60` 
      : '0 4px 15px rgba(0, 123, 255, 0.4)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 8px 16px;
  }

  @media (max-width: 360px) {
    font-size: 1rem;
    padding: 6px 12px;
  }
`;

const WithdrawButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.error 
    : 'linear-gradient(135deg, #dc3545, #c82333)'};
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? '#cc0000' 
      : 'linear-gradient(135deg, #c82333, #a71e2a)'};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
      ? `0 0 15px ${theme.colors.error}60` 
      : '0 4px 15px rgba(220, 53, 69, 0.4)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 8px 16px;
  }

  @media (max-width: 360px) {
    font-size: 1rem;
    padding: 6px 12px;
  }
`;

const LeaveButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.error 
    : 'linear-gradient(135deg, #f44336, #d32f2f)'};
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? '#cc0000' 
      : 'linear-gradient(135deg, #d32f2f, #b71c1c)'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 15px 30px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    font-size: 1rem;
  }
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.name === 'modernWarfare' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 212, 255, 0.3)'};
  margin-bottom: 30px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    margin-bottom: 25px;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 480px) {
    margin-bottom: 20px;
    gap: 2px;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  background: ${({ active, theme }) => {
    if (theme.name === 'modernWarfare') {
      return active ? theme.colors.primary + '20' : 'transparent';
    }
    return active ? 'rgba(0, 212, 255, 0.1)' : 'transparent';
  }};
  border: none;
  color: ${({ active, theme }) => {
    if (theme.name === 'modernWarfare') {
      return active ? theme.colors.primary : theme.colors.textSecondary;
    }
    return active ? '#00d4ff' : '#b0b0b0';
  }};
  padding: 15px 30px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid ${({ active, theme }) => {
    if (theme.name === 'modernWarfare') {
      return active ? theme.colors.primary : 'transparent';
    }
    return active ? '#00d4ff' : 'transparent';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  flex-shrink: 0;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : 'normal'};

  &:hover {
    color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff'};
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? theme.colors.primary + '10' 
      : 'rgba(0, 212, 255, 0.05)'};
  }

  @media (max-width: 768px) {
    padding: 15px 12px;
    font-size: 0.85rem;
    flex: 1;
    text-align: center;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
  }

  @media (max-width: 480px) {
    padding: 12px 8px;
    font-size: 0.8rem;
    min-width: 80px;
    gap: 1px;
  }
`;

const TabText = styled.span`
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  .short-text {
    display: none;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    .full-text {
      display: none;
    }
    
    .short-text {
      display: inline;
    }
  }
`;

const TabCount = styled.span`
  color: #888;
  font-size: 0.9rem;
  margin-left: 5px;

  @media (max-width: 768px) {
    margin-left: 0;
    font-size: 0.7rem;
    color: #666;
  }
`;

const TabContent = styled.div`
  min-height: 400px;
`;

const OverviewTab = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const ClanStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  backdrop-filter: none;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};
  position: relative;

  ${({ theme }) => theme.name === 'modernWarfare' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const StatTitle = styled.h3`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  margin: 0 0 15px 0;
  font-size: 1.2rem;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : 'normal'};

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 20px;
    text-align: center;
  }
`;

const RankInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
`;

const RankBadge = styled.span<{ color: string }>`
  background: ${({ theme, color }) => theme.name === 'modernWarfare' 
    ? `${color}20` 
    : color};
  color: ${({ theme }) => theme.name === 'modernWarfare' ? '#fff' : 'white'};
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  border: ${({ theme, color }) => theme.name === 'modernWarfare' ? `1px solid ${color}` : 'none'};
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  box-shadow: ${({ theme, color }) => theme.name === 'modernWarfare' ? `0 0 10px ${color}40` : 'none'};

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
  }
`;

const RankLevel = styled.span`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#b0b0b0'};
  font-size: 1.1rem;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const RankProgress = styled.div`
  margin-top: 15px;
`;

const ProgressBar = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  height: 8px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : '4px'};
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div<{ width: number }>`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.primary 
    : 'linear-gradient(90deg, #00d4ff, #0099cc)'};
  height: 100%;
  width: ${props => props.width}%;
  transition: width 0.3s ease;
  box-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
    ? `0 0 10px ${theme.colors.primary}80` 
    : 'none'};
`;

const ProgressText = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#b0b0b0'};
  text-align: center;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: 12px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const InfoLabel = styled.span`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888'};
  font-size: 0.9rem;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const InfoValue = styled.span`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.text : '#e0e0e0'};
  font-weight: bold;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const BadgeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 768px) {
    gap: 12px;
    justify-content: center;
  }
`;

const StatusBadge = styled.span<{ color: string }>`
  background: ${props => props.color};
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: bold;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 1rem;
    border-radius: 8px;
  }
`;

const EfficiencyBadge = styled.span<{ color: string }>`
  background: ${props => props.color};
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: bold;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 1rem;
    border-radius: 8px;
  }
`;

const ClanDescription = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};
  position: relative;

  ${({ theme }) => theme.name === 'modernWarfare' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const DescriptionTitle = styled.h3`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  margin: 0 0 15px 0;
  font-size: 1.2rem;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : 'normal'};

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 20px;
    text-align: center;
  }
`;

const DescriptionText = styled.p`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#b0b0b0'};
  line-height: 1.6;
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.7;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    text-align: left;
  }
`;

const ClanLeaderInfo = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  text-align: center;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};
  position: relative;

  ${({ theme }) => theme.name === 'modernWarfare' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const LeaderTitle = styled.h3`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : 'normal'};

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 15px;
  }
`;

const LeaderName = styled.div`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.text : '#e0e0e0'};
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 10px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  &:hover {
    color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff'};
    text-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
      ? `0 0 10px ${theme.colors.primary}80` 
      : '0 0 10px rgba(0, 212, 255, 0.5)'};
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const LeaderRole = styled.div`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888'};
  font-size: 0.9rem;
  margin-bottom: 10px;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 15px;
  }
`;

const SeniorOfficerName = styled.div`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.text : '#00d4ff'};
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 5px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  &:hover {
    color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.primary : '#ffffff'};
    text-shadow: ${({ theme }) => theme.name === 'modernWarfare' 
      ? `0 0 10px ${theme.colors.primary}80` 
      : '0 0 10px rgba(0, 212, 255, 0.5)'};
  }

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const SeniorOfficerRole = styled.div`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888'};
  font-size: 0.9rem;
  margin-bottom: 10px;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 15px;
  }
`;

const RemoveSeniorOfficerButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.error 
    : 'linear-gradient(135deg, #f44336, #d32f2f)'};
  border: none;
  color: white;
  padding: 10px 20px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 15px;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? '#cc0000' 
      : 'linear-gradient(135deg, #d32f2f, #b71c1c)'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
    margin-top: 20px;
  }
`;

const RemoveOfficerButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.warning 
    : 'linear-gradient(135deg, #FF9800, #F57C00)'};
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '6px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? '#e69500' 
      : 'linear-gradient(135deg, #F57C00, #E65100)'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
  }
`;

const AssignOfficerButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.secondary 
    : 'linear-gradient(135deg, #00d4ff, #0099cc)'};
  border: none;
  color: ${({ theme }) => theme.name === 'modernWarfare' ? '#000' : 'white'};
  padding: 10px 20px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? '#04b4c0' 
      : 'linear-gradient(135deg, #0099cc, #007aa3)'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
  }
`;

const LeaderSince = styled.div`
  color: #888;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const MembersTab = styled.div``;

// New layout components for sidebar design - Transparent theme
const MembersLayoutContainer = styled.div`
  display: flex;
  min-height: 600px;
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? 'rgba(5, 7, 10, 0.6)' 
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : '#00d4ff'};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MembersSidebar = styled.div`
  width: 280px;
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? 'rgba(5, 7, 10, 0.8)' 
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border-right: 1px solid ${({ theme }) => theme.name === 'modernWarfare' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 212, 255, 0.2)'};
  padding: 24px 0;

  @media (max-width: 768px) {
    width: 100%;
    padding: 16px 0;
  }
`;

const SidebarSection = styled.div`
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const SidebarSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : 'rgba(0, 212, 255, 0.7)'};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : '0.5px'};
  margin-bottom: 12px;
  padding: 0 4px;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
`;

const SidebarNavItem = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: ${({ active, theme }) => {
    if (theme.name === 'modernWarfare') {
      return active ? theme.colors.primary + '20' : 'transparent';
    }
    return active 
      ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.3), rgba(0, 180, 216, 0.3))' 
      : 'transparent';
  }};
  border: ${({ active, theme }) => {
    if (theme.name === 'modernWarfare') {
      return active ? `1px solid ${theme.colors.primary}60` : '1px solid transparent';
    }
    return active ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid transparent';
  }};
  padding: 14px 16px;
  margin-bottom: 4px;
  font-size: 15px;
  font-weight: 500;
  color: ${({ active, theme }) => {
    if (theme.name === 'modernWarfare') {
      return active ? theme.colors.primary : theme.colors.textSecondary;
    }
    return active ? '#00d4ff' : 'rgba(255, 255, 255, 0.7)';
  }};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  transition: all 0.2s ease;
  text-align: left;
  box-shadow: ${({ active, theme }) => {
    if (theme.name === 'modernWarfare') {
      return active ? `0 4px 12px ${theme.colors.primary}20` : 'none';
    }
    return active ? '0 4px 12px rgba(0, 212, 255, 0.2)' : 'none';
  }};
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ active, theme }) => {
      if (theme.name === 'modernWarfare') {
        return active ? theme.colors.primary + '20' : theme.colors.primary + '10';
      }
      return active 
        ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.3), rgba(0, 180, 216, 0.3))' 
        : 'rgba(255, 255, 255, 0.05)';
    }};
    color: ${({ active, theme }) => {
      if (theme.name === 'modernWarfare') {
        return active ? theme.colors.primary : theme.colors.text;
      }
      return active ? '#00d4ff' : 'rgba(255, 255, 255, 0.9)';
    }};
    border-color: ${({ active, theme }) => {
      if (theme.name === 'modernWarfare') {
        return active ? theme.colors.primary : theme.colors.primary + '40';
      }
      return active ? 'rgba(0, 212, 255, 0.5)' : 'rgba(255, 255, 255, 0.1)';
    }};
    transform: ${({ active }) => active ? 'none' : 'translateY(-1px)'};
  }
`;

const NavItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const NavItemIcon = styled.span`
  font-size: 16px;
`;

const NavItemText = styled.span``;

const NavBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.active 
    ? 'rgba(0, 212, 255, 0.2)' 
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.active ? '#00d4ff' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => props.active 
    ? 'rgba(0, 212, 255, 0.3)' 
    : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 12px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 600;
  min-width: 24px;
`;

const QuickActions = styled.div`
  margin-top: 24px;
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const QuickActionButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.3), rgba(0, 180, 216, 0.3));
  color: #00d4ff;
  border: 1px solid rgba(0, 212, 255, 0.4);
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.4), rgba(0, 180, 216, 0.4));
    border-color: rgba(0, 212, 255, 0.6);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 212, 255, 0.2);
  }
`;

const MembersContent = styled.div`
  flex: 1;
  padding: 32px;
  background: transparent;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const MemberCard = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};

  ${({ theme }) => theme.name === 'modernWarfare' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}

  &:hover {
    background: ${({ theme }) => theme.name === 'modernWarfare'
      ? 'rgba(5, 7, 10, 0.8)'
      : 'var(--legacy-card-bg)'};
    backdrop-filter: none;
    border-color: ${({ theme }) => theme.name === 'modernWarfare'
      ? theme.colors.secondary
      : theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.name === 'modernWarfare'
      ? `0 8px 25px ${theme.colors.secondary}20` 
      : '0 8px 25px rgba(0, 212, 255, 0.15)'};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const MemberHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 20px;
  }
`;

const MemberName = styled.h4`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  margin: 0;
  font-size: 1.1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : 'normal'};

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const MemberRole = styled.span<{ color: string }>`
  background: ${({ theme, color }) => theme.name === 'modernWarfare' 
    ? `${color}20` 
    : color};
  color: ${({ theme }) => theme.name === 'modernWarfare' ? '#fff' : 'white'};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '4px'};
  font-size: 0.8rem;
  font-weight: bold;
  border: ${({ theme, color }) => theme.name === 'modernWarfare' ? `1px solid ${color}` : 'none'};
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.9rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '6px'};
  }
`;

const MemberStats = styled.div`
  margin-bottom: 15px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;

  @media (max-width: 768px) {
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    margin-bottom: 0;
  }
`;

const StatLabel = styled.span`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888'};
  font-size: 0.9rem;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StatValue = styled.span`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.text : '#e0e0e0'};
  font-weight: bold;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FactionRanks = styled.div`
  margin-bottom: 15px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const FactionRank = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;

  @media (max-width: 768px) {
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    margin-bottom: 0;
  }
`;

const FactionLabel = styled.span`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888'};
  font-size: 0.9rem;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FactionRankName = styled.span`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.text : '#e0e0e0'};
  font-weight: bold;
  font-size: 0.9rem;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const MemberSince = styled.div`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888'};
  font-size: 0.8rem;
  text-align: center;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding-top: 15px;
  }
`;

const ProfileHint = styled.div`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  font-size: 0.75rem;
  text-align: center;
  margin-top: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
  font-weight: 500;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  ${MemberCard}:hover & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    opacity: 1;
    font-size: 0.8rem;
    margin-top: 10px;
  }
`;

const MemberActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 768px) {
    margin-top: 20px;
    flex-direction: column;
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const ConfirmActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? 'rgba(5, 7, 10, 0.6)' 
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : '#00d4ff'};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  width: 100%;
  max-width: 300px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 15px;
    gap: 8px;
  }
`;

const ConfirmText = styled.p`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.text : '#e0e0e0'};
  font-size: 1rem;
  text-align: center;
  margin-bottom: 15px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ConfirmButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.secondary 
    : 'linear-gradient(135deg, #00d4ff, #0099cc)'};
  border: none;
  color: ${({ theme }) => theme.name === 'modernWarfare' ? '#000' : 'white'};
  padding: 10px 20px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? '#04b4c0' 
      : 'linear-gradient(135deg, #0099cc, #007aa3)'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
  }
`;

const CancelButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.surface 
    : 'linear-gradient(135deg, #f44336, #d32f2f)'};
  border: ${({ theme }) => theme.name === 'modernWarfare' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'};
  color: white;
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'linear-gradient(135deg, #d32f2f, #b71c1c)'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 15px 30px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    font-size: 1rem;
  }
`;

const RemoveButton = styled.button`
  background: ${({ theme }) => theme.name === 'modernWarfare' 
    ? theme.colors.error 
    : 'linear-gradient(135deg, #f44336, #d32f2f)'};
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '8px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' : 'none'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.name === 'modernWarfare' 
      ? '#cc0000' 
      : 'linear-gradient(135deg, #d32f2f, #b71c1c)'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 15px 30px;
    font-size: 1.1rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '10px'};
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    font-size: 1rem;
  }
`;

const AchievementsTab = styled.div``;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const AchievementCard = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};
  position: relative;

  ${({ theme }) => theme.name === 'modernWarfare' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const AchievementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 15px;
  }
`;

const AchievementName = styled.h4`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.secondary : '#00d4ff'};
  margin: 0;
  font-size: 1.1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};
  letter-spacing: ${({ theme }) => theme.name === 'modernWarfare' ? '1px' : 'normal'};

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const AchievementRarity = styled.span<{ color: string }>`
  background: ${({ theme, color }) => theme.name === 'modernWarfare' 
    ? `${color}20` 
    : color};
  color: ${({ theme }) => theme.name === 'modernWarfare' ? '#fff' : 'white'};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '4px'};
  font-size: 0.8rem;
  font-weight: bold;
  border: ${({ theme, color }) => theme.name === 'modernWarfare' ? `1px solid ${color}` : 'none'};
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};
  text-transform: ${({ theme }) => theme.name === 'modernWarfare' ? 'uppercase' : 'none'};

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.9rem;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '2px' : '6px'};
  }
`;

const AchievementDescription = styled.p`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#b0b0b0'};
  margin: 10px 0;
  font-size: 0.9rem;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.5;
    margin: 15px 0;
  }
`;

const AchievementDate = styled.div`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888'};
  font-size: 0.8rem;
  text-align: right;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 0.9rem;
    text-align: center;
  }
`;

const EmptyAchievements = styled.div`
  text-align: center;
  padding: 50px 20px;

  @media (max-width: 768px) {
    padding: 40px 15px;
  }
`;

const EmptyAchievementsIcon = styled.div`
  font-size: 4rem;
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888'};
  margin-bottom: 20px;
`;

const EmptyAchievementsTitle = styled.h3`
  color: ${({ theme }) => theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888'};
  margin-bottom: 15px;
  font-family: ${({ theme }) => theme.name === 'modernWarfare' ? "'Chakra Petch', sans-serif" : 'inherit'};

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
`;

const EmptyAchievementsDescription = styled.p`
  color: #666;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.7;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  font-size: 1.2rem;
  color: #00d4ff;
  margin: 50px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #ff4444;
  font-size: 1.1rem;
  margin: 20px 0;
`;

const EmptyMembers = styled.div`
  text-align: center;
  padding: 50px 20px;

  @media (max-width: 768px) {
    padding: 40px 15px;
  }
`;

const EmptyTitle = styled.h3`
  color: #888;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
`;

const EmptyDescription = styled.p`
  color: #666;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.7;
  }
`;

const SettingsTab = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};
  position: relative;

  ${({ theme }) => theme.name === 'modernWarfare' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const SettingsTitle = styled.h3`
  color: #00d4ff;
  margin: 0 0 15px 0;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 20px;
    text-align: center;
  }
`;

const SettingsDescription = styled.p`
  color: #b0b0b0;
  margin: 0 0 20px 0;
  font-size: 0.9rem;
  line-height: 1.6;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.7;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    text-align: left;
  }
`;

const SettingsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  color: #888;
  font-size: 0.9rem;
  margin-bottom: 5px;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 8px;
  }
`;

const FormInput = styled.input`
  background: var(--legacy-card-bg);
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 8px;
  padding: 10px 15px;
  color: #e0e0e0;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
  }

  &::placeholder {
    color: #888;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 1.1rem;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 10px 15px;
    font-size: 1rem;
  }
`;

const FormTextarea = styled.textarea`
  background: var(--legacy-card-bg);
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 8px;
  padding: 10px 15px;
  color: #e0e0e0;
  font-size: 1rem;
  line-height: 1.6;
  resize: vertical;
  min-height: 80px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
  }

  &::placeholder {
    color: #888;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 1.1rem;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 10px 15px;
    font-size: 1rem;
  }
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 15px;

  @media (max-width: 768px) {
    margin-top: 20px;
  }
`;

const CurrentLogo = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 15px;
  overflow: hidden;
  border: 2px solid #00d4ff;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #1a1a1a;

  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    width: 120px;
    height: 120px;
    border-radius: 15px;
  }
`;

const LogoPreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LogoLabel = styled.span`
  position: absolute;
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 3px 10px;
  border-radius: 5px;
  font-size: 0.7rem;
  font-weight: bold;
  white-space: nowrap;
`;

const FileInputWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--legacy-card-bg);
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  color: #e0e0e0;
  padding: 15px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  width: 100%;
  min-height: 60px;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
  font-size: 1rem;

  &:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:active {
    background: rgba(0, 212, 255, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 18px 24px;
    font-size: 1.1rem;
    border-radius: 10px;
    min-height: 70px;
  }

  @media (max-width: 480px) {
    padding: 15px 20px;
    font-size: 1rem;
    min-height: 60px;
  }
`;

const FileInputHint = styled.p`
  color: #888;
  font-size: 0.85rem;
  margin-top: 8px;
  text-align: center;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-top: 10px;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const SaveButton = styled.button`
  background: linear-gradient(135deg, #4CAF50, #45a049);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #45a049, #3d8b40);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 15px 30px;
    font-size: 1.1rem;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    font-size: 1rem;
  }
`;

const LocationInfo = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};
  position: relative;

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const LocationTitle = styled.h3`
  color: #00d4ff;
  margin: 0 0 15px 0;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 20px;
    text-align: center;
  }
`;

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const LocationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: 12px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const LocationLabel = styled.span`
  color: #888;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const LocationValue = styled.span`
  color: #e0e0e0;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const LocationSecondaryBases = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-column: 1 / -1; /* Занимает всю ширину грида */

  @media (max-width: 768px) {
    padding: 12px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const SecondaryBasesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SecondaryBase = styled.span`
  background: rgba(0, 212, 255, 0.1);
  border: 2px solid #00d4ff;
  color: #00d4ff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: bold;

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.9rem;
    border-radius: 6px;
  }
`;

// Styled-компоненты для системы новостей

// Компоненты для секции новостей в Overview
const NewsSection = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};
  position: relative;

  ${({ theme }) => theme.name === 'modernWarfare' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const NewsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    margin-bottom: 25px;
  }
`;

const NewsSectionTitle = styled.h3`
  color: #00d4ff;
  margin: 0;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const CreateNewsButton = styled.button`
  background: linear-gradient(135deg, #4CAF50, #45a049);
  border: none;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #45a049, #3d8b40);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 1rem;
    border-radius: 10px;
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const NewsCard = styled.div<{ type: 'Public' | 'Internal' }>`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${props => props.type === 'Public' ? 'rgba(0, 204, 0, 0.3)' : 'rgba(255, 165, 0, 0.3)'};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '10px'};
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;

  &:hover {
    background: ${({ theme }) => theme.name === 'modernWarfare'
      ? 'rgba(5, 7, 10, 0.8)'
      : 'rgba(26, 35, 46, 0.9)'};
    backdrop-filter: none;
    border-color: ${props => props.type === 'Public' ? '#00cc00' : '#ffa500'};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  }
`;

const NewsCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const NewsCardTitle = styled.h4`
  color: #ffffff;
  margin: 0;
  font-size: 1rem;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const NewsCardBadge = styled.span<{ type: 'Public' | 'Internal' }>`
  background: ${props => props.type === 'Public' ? 'rgba(0, 204, 0, 0.2)' : 'rgba(255, 165, 0, 0.2)'};
  border: 1px solid ${props => props.type === 'Public' ? '#00cc00' : '#ffa500'};
  color: ${props => props.type === 'Public' ? '#00cc00' : '#ffa500'};
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
  text-transform: uppercase;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 4px 10px;
    font-size: 0.8rem;
    border-radius: 6px;
  }
`;

const NewsCardContent = styled.p`
  color: #b0b0b0;
  margin: 10px 0;
  font-size: 0.9rem;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.5;
    margin: 15px 0;
  }
`;

const NewsCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  font-size: 0.8rem;
  color: #888;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    font-size: 0.9rem;
  }
`;

const NewsCardAuthor = styled.span`
  color: #888;
`;

const NewsCardDate = styled.span`
  color: #666;
`;

const NewsCardActions = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 5px;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${NewsCard}:hover & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    position: static;
    opacity: 1;
    margin-top: 15px;
    justify-content: flex-end;
  }
`;

const EditNewsButton = styled.button`
  background: rgba(0, 212, 255, 0.8);
  border: 1px solid #00d4ff;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 212, 255, 1);
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.8rem;
    border-radius: 6px;
  }
`;

const DeleteNewsButton = styled.button`
  background: rgba(244, 67, 54, 0.8);
  border: 1px solid #f44336;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(244, 67, 54, 1);
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.8rem;
    border-radius: 6px;
  }
`;

const NoNewsMessage = styled.div`
  text-align: center;
  color: #888;
  font-size: 1rem;
  padding: 40px 20px;
  grid-column: 1 / -1;

  @media (max-width: 768px) {
    padding: 30px 15px;
    font-size: 1.1rem;
  }
`;

const CreateNewsLink = styled.button`
  background: none;
  border: none;
  color: #00d4ff;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  padding: 0;
  margin: 0;

  &:hover {
    color: #ffffff;
  }
`;

// Компоненты для News вкладки
const NewsTab = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};
  position: relative;

  ${({ theme }) => theme.name === 'modernWarfare' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '15px'};
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const NewsTabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    margin-bottom: 25px;
  }
`;

const NewsTabTitle = styled.h2`
  color: #00d4ff;
  margin: 0;
  font-size: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const NewsTabContent = styled.div`
  min-height: 300px;
`;

const NewsTabDescription = styled.p`
  color: #b0b0b0;
  margin: 0 0 30px 0;
  font-size: 1rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 35px;
  }
`;

const NewsComingSoon = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #888;

  @media (max-width: 768px) {
    padding: 50px 15px;
  }
`;

const NewsComingSoonIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const NewsComingSoonTitle = styled.h3`
  color: #00d4ff;
  margin: 0 0 15px 0;
  font-size: 1.3rem;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 20px;
  }
`;

const NewsComingSoonDescription = styled.p`
  color: #b0b0b0;
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.7;
  }
`;

// Withdraw Application Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: rgba(0, 25, 0, 0.95);
  border: 2px solid #00d4ff;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 212, 255, 0.2);
  animation: scaleIn 0.3s ease-in-out;
  
  @keyframes scaleIn {
    from { 
      opacity: 0;
      transform: scale(0.9);
    }
    to { 
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @media (max-width: 768px) {
    width: 95%;
    margin: 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
`;

const ModalTitle = styled.h3`
  font-family: 'Courier New', monospace;
  color: #00d4ff;
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ff4444;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
  text-align: center;
`;

const WarningIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
`;

const ModalText = styled.p`
  font-family: 'Courier New', monospace;
  color: #ffffff;
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 15px 0;
  
  strong {
    color: #00d4ff;
  }
`;

const ModalSubtext = styled.p`
  font-family: 'Courier New', monospace;
  color: #888;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 15px;
  padding: 20px 25px;
  border-top: 1px solid rgba(0, 212, 255, 0.2);
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 10px;
  }
`;

const CancelModalButton = styled.button`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  padding: 12px 24px;
  background: rgba(136, 136, 136, 0.1);
  color: #888;
  border: 2px solid #888;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 6px;
  flex: 1;
  
  &:hover {
    background: rgba(136, 136, 136, 0.2);
    color: #aaa;
    border-color: #aaa;
    box-shadow: 0 0 10px rgba(136, 136, 136, 0.3);
  }
`;

const WithdrawModalButton = styled.button<{ disabled?: boolean }>`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'rgba(255, 68, 68, 0.5)' : 'rgba(255, 68, 68, 0.1)'};
  color: ${props => props.disabled ? '#ff8888' : '#ff4444'};
  border: 2px solid ${props => props.disabled ? '#ff8888' : '#ff4444'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 6px;
  flex: 1;
  
  &:hover:not(:disabled) {
    background: rgba(255, 68, 68, 0.2);
    box-shadow: 0 0 10px rgba(255, 68, 68, 0.3);
  }
`;

// Styled-компоненты для секции достижений
const AchievementsSection = styled.div`
  background: ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(5, 7, 10, 0.6)'
    : 'var(--legacy-card-bg)'};
  backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.name === 'modernWarfare'
    ? 'rgba(255, 255, 255, 0.1)'
    : cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  padding: 20px;
  margin-bottom: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  clip-path: ${({ theme }) => theme.name === 'modernWarfare' ? 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' : 'none'};
  position: relative;

  ${({ theme }) => theme.name === 'modernWarfare' && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.primary};
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 1px;
      background: ${theme.colors.secondary};
    }
  `}

  @media (max-width: 768px) {
    padding: 15px;
    margin-bottom: 15px;
    border-radius: ${({ theme }) => theme.name === 'modernWarfare' ? '4px' : '12px'};
  }
`;

const AchievementsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    margin-bottom: 25px;
  }
`;

const AchievementsSectionTitle = styled.h3`
  color: #00d4ff;
  margin: 0;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const ShadowGridWrapper = styled.div<{ $enabled: boolean }>`
  ${({ $enabled, theme }) => {
    if (!$enabled) return '';
    const panelBg = 'rgba(5, 7, 10, 0.85)';
    const panelBorder = theme.profile?.cards?.border ?? 'rgba(0, 212, 255, 0.35)';
    const panelShadow = '0 25px 60px rgba(0, 0, 0, 0.65)';

    return css`
      ${Header},
      ${StatCard},
      ${LocationInfo},
      ${ClanDescription},
      ${ClanLeaderInfo},
      ${NewsSection},
      ${MembersLayoutContainer},
      ${MembersSidebar},
      ${MemberCard},
      ${AchievementCard},
      ${AchievementsSection},
      ${NewsCard},
      ${NewsTab},
      ${SettingsTab} {
        background: ${panelBg};
        border: 1px solid ${panelBorder};
        border-radius: 18px;
        box-shadow: ${panelShadow};
        backdrop-filter: none;
      }

      ${MembersLayoutContainer} {
        border-radius: 20px;
        overflow: hidden;
      }

      ${MembersSidebar} {
        border-right: 1px solid ${panelBorder};
        border-radius: 0;
        background: rgba(0, 6, 12, 0.55);
      }

      ${Tabs} {
        background: rgba(0, 6, 14, 0.45);
        border: 1px solid ${panelBorder};
        border-radius: 16px;
        padding: 6px;
        gap: 10px;
        box-shadow: inset 0 0 25px rgba(0, 0, 0, 0.35);
      }

      ${Tab} {
        border: 1px solid transparent;
        border-radius: 12px;
        background: transparent;
        color: rgba(255, 255, 255, 0.75);
        text-transform: uppercase;
        letter-spacing: 1px;
        font-family: 'Courier New', monospace;
        transition: all 0.25s ease;
      }

      ${Tab}[active='true'] {
        background: ${theme.colors.primary};
        color: #00121f;
        border-color: ${theme.colors.primary};
        box-shadow: 0 0 25px rgba(0, 212, 255, 0.4);
      }

      ${Tab}:hover {
        background: rgba(0, 212, 255, 0.1);
        color: ${theme.colors.primary};
      }

      ${TabCount} {
        background: rgba(0, 0, 0, 0.4);
        border-radius: 999px;
        border: 1px solid ${panelBorder};
        color: ${theme.colors.text};
        padding: 2px 10px;
        margin-left: 8px;
        font-family: 'Courier New', monospace;
      }

      ${BackButton} {
        background: ${panelBg};
        border-color: ${panelBorder};
        color: ${theme.colors.primary};
        border-radius: 12px;
        letter-spacing: 1px;
      }

      ${BackButton}:hover {
        box-shadow: ${panelShadow};
        border-color: ${theme.colors.primary};
      }

      ${SidebarNavItem} {
        border-radius: 12px;
        border: 1px solid transparent;
        background: rgba(0, 6, 12, 0.4);
        color: rgba(255, 255, 255, 0.7);
      }

      ${SidebarNavItem}[active='true'] {
        background: rgba(0, 212, 255, 0.18);
        border-color: ${panelBorder};
        color: ${theme.colors.primary};
        box-shadow: 0 12px 28px rgba(0, 212, 255, 0.28);
      }

      ${SidebarNavItem}:hover {
        background: rgba(0, 212, 255, 0.08);
        border-color: ${panelBorder};
        color: ${theme.colors.primary};
      }

      ${QuickActionButton} {
        border-radius: 12px;
        border: 1px solid ${panelBorder};
        background: rgba(0, 212, 255, 0.12);
        color: ${theme.colors.primary};
        box-shadow: none;
      }

      ${FormInput}, ${FormTextarea}, ${FileInputLabel} {
        background: ${panelBg};
        border: 1px solid ${panelBorder};
        color: ${theme.colors.text};
        border-radius: 12px;
      }
    `;
  }}
`;


export default ClanDetailPage; 
